import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  fetchPmsProjects,
  fetchPmsTasks,
  PMS_APP_URL,
  type PmsProject,
  type PmsTask,
} from '@/services/pmsApi';

type Tab = 'projects' | 'tasks';

const STATUS_COLORS: Record<string, string> = {
  DONE: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400',
  COMPLETED: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400',
  IN_PROGRESS: 'bg-[#EEF2FF] text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]',
  TODO: 'bg-[#F3F4F8] text-slate-600 dark:bg-white/5 dark:text-slate-300',
  BACKLOG: 'bg-[#F3F4F8] text-slate-500 dark:bg-white/5 dark:text-slate-400',
  CANCELLED: 'bg-rose-50 text-rose-600 dark:bg-rose-500/10 dark:text-rose-400',
  ACTIVE: 'bg-[#EEF2FF] text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]',
  ON_HOLD: 'bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400',
  ARCHIVED: 'bg-[#F3F4F8] text-slate-500 dark:bg-white/5 dark:text-slate-400',
};

const PRIORITY_COLORS: Record<string, string> = {
  HIGH: 'text-red-500',
  MEDIUM: 'text-amber-500',
  LOW: 'text-slate-400',
  URGENT: 'text-rose-600',
};

function statusChip(status?: string | null) {
  if (!status) return null;
  const label = status.replace(/_/g, ' ');
  const cls = STATUS_COLORS[status.toUpperCase()] ?? 'bg-[#F3F4F8] text-slate-600 dark:bg-white/5 dark:text-slate-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${cls}`}>
      {label}
    </span>
  );
}

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

export function PmsPanel({ isOpen, onClose }: Props) {
  const [tab, setTab] = useState<Tab>('projects');
  const [projects, setProjects] = useState<PmsProject[]>([]);
  const [tasks, setTasks] = useState<PmsTask[]>([]);
  const [selectedProject, setSelectedProject] = useState<PmsProject | null>(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [present, setPresent] = useState(isOpen);
  const [shown, setShown] = useState(false);

  const loadProjects = useCallback(async (q = '') => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPmsProjects({ search: q });
      setProjects(data);
    } catch {
      setError('Failed to load projects from PMS.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTasks = useCallback(async (projectId?: string, q = '') => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPmsTasks({ projectId, search: q });
      setTasks(data);
    } catch {
      setError('Failed to load tasks from PMS.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    if (tab === 'projects') {
      void loadProjects();
    } else {
      void loadTasks(selectedProject?.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tab]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) {
      setSearch('');
      setSelectedProject(null);
    }
  }, [isOpen]);

  const handleSearch = (q: string) => {
    setSearch(q);
    if (tab === 'projects') {
      void loadProjects(q);
    } else {
      void loadTasks(selectedProject?.id, q);
    }
  };

  const handleSelectProject = (project: PmsProject) => {
    setSelectedProject(project);
    setTab('tasks');
    setSearch('');
    void loadTasks(project.id);
  };

  const handleBackToProjects = () => {
    setSelectedProject(null);
    setTab('projects');
    setSearch('');
    void loadProjects();
  };

  // Presence-based enter/exit: the panel is parent-controlled via `isOpen`, so we
  // keep it mounted for the slide-out duration after it closes.
  if (isOpen && !present) setPresent(true);
  useEffect(() => {
    if (isOpen) {
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    const t = window.setTimeout(() => setPresent(false), 300);
    return () => window.clearTimeout(t);
  }, [isOpen]);

  if (!present) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <button
        type="button"
        className={`fixed inset-0 z-[10050] cursor-default border-0 bg-[#0f172b]/30 p-0 backdrop-blur-[3px] transition-opacity duration-300 ease-out ${shown ? 'opacity-100' : 'opacity-0'}`}
        aria-label="Close PMS panel"
        onClick={onClose}
      />

      {/* Panel */}
      <div
        className={`fixed inset-y-0 right-0 z-[10060] flex w-full max-w-[460px] flex-col overflow-hidden rounded-l-3xl border-l border-slate-100 bg-[#F6F7FB] shadow-[-20px_0_60px_-30px_rgba(15,23,43,0.35)] transition-transform dark:border-white/5 dark:bg-background duration-300 ease-out will-change-transform ${shown ? 'translate-x-0' : 'translate-x-full'}`}
        role="dialog"
        aria-modal="true"
        aria-label="PMS Projects & Tasks"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-100 bg-white px-6 py-5 dark:border-white/5 dark:bg-card">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#5B6CF6] to-[#8B5CF6]">
              <PmsIcon />
            </div>
            <div>
              <h2 className="text-lg font-bold leading-tight tracking-tight text-[#0f172b] dark:text-white">
                {selectedProject ? selectedProject.name : 'PMS'}
              </h2>
              {selectedProject && (
                <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-400">Tasks</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {PMS_APP_URL && (
              <a
                href={PMS_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 rounded-full bg-[#F3F4F8] px-3 py-1.5 text-[11px] font-semibold text-slate-600 transition-colors hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
                title="Open PMS app"
              >
                Open PMS
                <ExternalLinkIcon />
              </a>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F3F4F8] text-slate-500 transition-colors hover:bg-[#E8EAF2] dark:bg-white/5 dark:text-slate-300 dark:hover:bg-white/10"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Back button when viewing tasks for a project */}
        {selectedProject && (
          <div className="px-6 pt-4">
            <button
              type="button"
              onClick={handleBackToProjects}
              className="inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-600 transition-colors hover:text-[#5B47E8] dark:bg-card dark:text-slate-300 dark:hover:text-[#8B7CFF]"
            >
              <BackIcon />
              All Projects
            </button>
          </div>
        )}

        {/* Tabs — only show when not drilled into a project */}
        {!selectedProject && (
          <div className="mx-6 mt-4 flex w-fit items-center gap-1 rounded-full bg-[#ECEEF5] p-1 dark:bg-white/5">
            {(['projects', 'tasks'] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  setTab(t);
                  setSearch('');
                  if (t === 'projects') void loadProjects();
                  else void loadTasks();
                }}
                className={`rounded-full px-5 py-1.5 text-[13px] font-semibold capitalize transition-all ${
                  tab === t
                    ? 'bg-white text-[#5B47E8] shadow-sm dark:bg-[#1C1F3F] dark:text-[#8B7CFF]'
                    : 'text-slate-500 hover:text-[#0f172b] dark:text-slate-400 dark:hover:text-white'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Search */}
        <div className="px-6 py-4">
          <div className="relative">
            <SearchIcon />
            <input
              value={search}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={tab === 'projects' ? 'Search projects…' : 'Search tasks…'}
              className="h-11 w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 text-[13px] text-[#0f172b] outline-none transition placeholder:text-slate-400 focus:border-[#5B6CF6] focus:ring-4 focus:ring-[#5B6CF6]/10 dark:border-white/10 dark:bg-card dark:text-white"
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <SpinnerIcon />
            </div>
          ) : error ? (
            <div className="mx-6 flex flex-col items-center justify-center gap-4 rounded-3xl border border-dashed border-rose-200 bg-white px-6 py-12 text-center dark:border-rose-500/30 dark:bg-card">
              <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-500 dark:bg-rose-500/10 dark:text-rose-400"><svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round"><path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg></span>
              <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">{error}</p>
              <button
                type="button"
                onClick={() => tab === 'projects' ? void loadProjects(search) : void loadTasks(selectedProject?.id, search)}
                className="rounded-full bg-gradient-to-r from-[#5B6CF6] to-[#8B5CF6] px-5 py-2 text-[12px] font-semibold text-white transition hover:opacity-95 active:scale-95"
              >
                Retry
              </button>
            </div>
          ) : tab === 'projects' && !selectedProject ? (
            projects.length === 0 ? (
              <EmptyState label="No projects found" />
            ) : (
              <ul className="space-y-3 px-6 pb-6">
                {projects.map((project) => (
                  <li key={project.id}>
                    <button
                      type="button"
                      onClick={() => handleSelectProject(project)}
                      className="group flex w-full items-start gap-3 rounded-2xl border border-slate-100 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#5B6CF6]/40 hover:shadow-md dark:border-white/5 dark:bg-card"
                    >
                      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EEF2FF] dark:bg-[#1C1F3F]">
                        <FolderIcon />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="truncate text-sm font-semibold text-[#0f172b] transition-colors group-hover:text-[#5B47E8] dark:text-white">
                            {project.name}
                          </span>
                          {statusChip(project.status)}
                        </div>
                        {project.description && (
                          <p className="mt-0.5 text-[12px] text-slate-500 line-clamp-1 dark:text-slate-400">
                            {project.description}
                          </p>
                        )}
                        {(project.startDate || project.endDate) && (
                          <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                            {project.startDate ? formatDate(project.startDate) : '?'}
                            {project.endDate ? ` → ${formatDate(project.endDate)}` : ''}
                          </p>
                        )}
                      </div>
                      <ChevronRightIcon />
                    </button>
                  </li>
                ))}
              </ul>
            )
          ) : (
            tasks.length === 0 ? (
              <EmptyState label="No tasks found" />
            ) : (
              <ul className="space-y-3 px-6 pb-6">
                {tasks.map((task) => (
                  <li key={task.id} className="rounded-2xl border border-slate-100 bg-white p-4 transition-all hover:-translate-y-0.5 hover:border-[#5B6CF6]/40 hover:shadow-md dark:border-white/5 dark:bg-card">
                    <div className="flex items-start gap-3">
                      <TaskCheckIcon status={task.status} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="truncate text-[13px] font-semibold text-[#0f172b] dark:text-white">
                            {task.name}
                          </span>
                          {task.priority && (
                            <span className={`text-[10px] font-bold uppercase ${PRIORITY_COLORS[task.priority.toUpperCase()] ?? 'text-muted-foreground'}`}>
                              {task.priority}
                            </span>
                          )}
                        </div>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          {statusChip(task.status)}
                          {!selectedProject && task.project?.name && (
                            <span className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                              {task.project.name}
                            </span>
                          )}
                          {task.dueDate && (
                            <span className="text-[11px] text-slate-500 dark:text-slate-400">
                              Due {formatDate(task.dueDate)}
                            </span>
                          )}
                        </div>
                      </div>
                      {PMS_APP_URL && (
                        <a
                          href={`${PMS_APP_URL}/tasks/${task.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="shrink-0 text-slate-400 transition-colors hover:text-[#5B47E8]"
                          title="Open in PMS"
                        >
                          <ExternalLinkIcon />
                        </a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )
          )}
        </div>

        {/* Footer */}
        {PMS_APP_URL && (
          <div className="border-t border-slate-100 bg-white px-6 py-4 dark:border-white/5 dark:bg-card">
            <a
              href={PMS_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-11 w-full items-center justify-center gap-2 rounded-full bg-[#EEF2FF] text-[13px] font-semibold text-[#5B47E8] transition-colors hover:bg-[#E0E7FF] dark:bg-[#1C1F3F] dark:text-[#8B7CFF] dark:hover:bg-[#262a52]"
            >
              Open Project Management
              <ArrowRightIcon />
            </a>
          </div>
        )}
      </div>
    </>,
    document.body
  );
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="mx-6 flex flex-col items-center justify-center gap-3 rounded-3xl border border-dashed border-[#5B6CF6]/30 bg-white px-6 py-14 text-center dark:border-[#8B7CFF]/30 dark:bg-card">
      <span aria-hidden="true" className="flex h-12 w-12 items-center justify-center rounded-full bg-[#EEF2FF] text-[#5B47E8] dark:bg-[#1C1F3F] dark:text-[#8B7CFF]"><svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg></span>
      <p className="text-sm font-semibold text-slate-500 dark:text-slate-400">{label}</p>
    </div>
  );
}

function TaskCheckIcon({ status }: { status?: string | null }) {
  const done = status?.toUpperCase() === 'DONE' || status?.toUpperCase() === 'COMPLETED';
  return (
    <div className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${done ? 'border-emerald-500 bg-emerald-500' : 'border-border'}`}>
      {done && (
        <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
      )}
    </div>
  );
}

// Icons
function PmsIcon() { return <svg className="h-5 w-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>; }
function CloseIcon() { return <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>; }
function ExternalLinkIcon() { return <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>; }
function BackIcon() { return <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>; }
function ChevronRightIcon() { return <svg className="h-4 w-4 shrink-0 text-muted-foreground mt-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>; }
function FolderIcon() { return <svg className="h-4 w-4 text-[#5B47E8] dark:text-[#8B7CFF]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" /></svg>; }
function ArrowRightIcon() { return <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>; }
function SpinnerIcon() { return <svg className="h-6 w-6 animate-spin text-primary" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>; }
function SearchIcon() { return <svg className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><circle cx="11" cy="11" r="8" strokeWidth="2" /><path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" /></svg>; }
